import React, { useEffect } from 'react';
import { useForm, FormProvider, FormState, UseFormReturn } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { registerSearchFirms, password } from 'routes/routePaths';
import { FormInputWrapper, TextInput } from 'form/infrastructure/reactHookForm';
import { Button, Heading, Text } from 'lmc-ui-library-rebrand';
import { FIELD, FIELD_LABEL } from './types';
import useYupValidationResolver from 'form/yup/reactHookFormResolver';
import { Form } from './types';
import { validationSchema } from './validationSchema';
import MultilineError from '../../MultilineError/MultilineError';
import ErrorManager from 'shared/Messages/ErrorManager';
import { Loader } from 'form/components/Select/Select.styled';
import styled from 'styled-components';
import './SignInForm.scss';

interface Props {
  onLogin: (values: Form) => void;
  isLogging: boolean;
  error: any;
  className: string;
}

const FormWrapper = styled.div`
  button[disabled] {
    background-color: #b2b2b2;
    color: #d2d2d2;
    border: none;

    &:hover {
      background-color: #b2b2b2;
      color: #d2d2d2;
    }
  }
`;

const SignInForm: React.FC<Props> = ({ onLogin, error, isLogging, className }) => {
  const methods: UseFormReturn<Form> & { formState: FormState<Form> & { errors: any } } = useForm<Form>({
    mode: 'onSubmit',
    resolver: useYupValidationResolver(validationSchema()),
    defaultValues: { [FIELD.USERNAME]: null, [FIELD.PASSWORD]: null },
  });

  const { handleSubmit, setError } = methods;

  const onSubmit = handleSubmit((data: Form) => onLogin(data));

  useEffect(() => {
    if (error) {
      setError(FIELD.USERNAME, {
        message: undefined,
      });
      setError(FIELD.PASSWORD, {
        message: undefined,
      });
    }
  }, [error, setError]);

  return (
    <FormWrapper className={className}>
      <FormProvider<Form> {...methods}>
        <form onSubmit={onSubmit}>
          <Heading className="signInheading" weight="bold" mode="title">
            Sign in
          </Heading>
          <Text color="var(--color-indigo-t11)">
            Don’t have an account?{' '}
            <Link to={registerSearchFirms}>
              <Text weight="bold" color="var(--color-coral-rb)" className="underline">
                Register with us
              </Text>
            </Link>
          </Text>

          <FormInputWrapper title={FIELD_LABEL.USERNAME} name={FIELD.USERNAME} newUi>
            <TextInput newUi />
          </FormInputWrapper>

          <FormInputWrapper title={FIELD_LABEL.PASSWORD} name={FIELD.PASSWORD} newUi>
            <TextInput type="password" newUi />
          </FormInputWrapper>

          <Button
            borderVariant="rectangle"
            mode="primary"
            width="100%"
            mt={4}
            mb={5}
            disabled={isLogging}
            className="signInBtn">
            {!isLogging && 'Sign in'}
            {isLogging && <Loader style={{ margin: '0 auto' }} />}
          </Button>

          <Text color="var(--color-indigo-t11)">
            <Link className="coral" to={password.forgot}>
              <Text weight="bold" color="var(--color-coral-rb)" className="underline">
                Forgot password?
              </Text>
            </Link>
          </Text>
        </form>
        {error ? error.status === 401 ? <MultilineError /> : <ErrorManager error={error} /> : null}
      </FormProvider>
    </FormWrapper>
  );
};

export default SignInForm;
